import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/label';
import { withdrawalService } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const RetraitInstantaneTab = ({ onRefresh }) => {
  const { user } = useAuth();
  const [soldeInfo, setSoldeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [montant, setMontant] = useState('');
  const [walletType, setWalletType] = useState('orange_money');
  const [walletNumber, setWalletNumber] = useState('');

  // Wallets supportés par ChapChapPay
  const walletsDisponibles = [
    { value: 'orange_money', label: 'Orange Money', icon: '🟠' },
    { value: 'mtn_momo', label: 'MTN MoMo', icon: '🟡' }
  ];

  const fetchSoldeInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await withdrawalService.getSoldeDisponible();
      setSoldeInfo(data);
    } catch (err) {
      console.error('Erreur chargement solde:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement du solde');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSoldeInfo();
  }, [fetchSoldeInfo]);

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montant || 0);
  };

  // Calculer la commission (5%)
  const calculateCommission = (amount) => {
    const montantNum = parseFloat(amount) || 0;
    const commission = Math.round(montantNum * 0.05);
    const montantNet = montantNum - commission;
    return { commission, montantNet };
  };

  const { commission, montantNet } = calculateCommission(montant);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validations
    const montantNum = parseFloat(montant);
    
    if (!montantNum || montantNum <= 0) {
      setError('Veuillez entrer un montant valide');
      return;
    }

    if (montantNum < 10000) {
      setError('Le montant minimum est de 10 000 GNF');
      return;
    }

    if (montantNum > (soldeInfo?.solde_disponible || 0)) {
      setError(`Montant insuffisant. Disponible: ${formatMontant(soldeInfo?.solde_disponible)}`);
      return;
    }

    if (!walletNumber || walletNumber.length < 9) {
      setError('Veuillez entrer un numéro de wallet valide (minimum 9 chiffres)');
      return;
    }

    setSubmitting(true);

    try {
      const response = await withdrawalService.initiateWithdrawal({
        employe_id: user?.user_id,
        montant: montantNum,
        wallet_type: walletType,
        wallet_number: walletNumber,
        description: `Avance salaire ${new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`
      });

      setSuccess({
        message: response.message,
        reference: response.reference,
        montantNet: response.montant_net
      });

      // Reset form
      setMontant('');
      setWalletNumber('');

      // Refresh solde
      await fetchSoldeInfo();
      
      if (onRefresh) {
        onRefresh();
      }

    } catch (err) {
      console.error('Erreur retrait:', err);
      setError(err.response?.data?.detail || 'Erreur lors du traitement du retrait');
    } finally {
      setSubmitting(false);
    }
  };

  const setMontantPredefini = (pourcentage) => {
    if (soldeInfo?.solde_disponible) {
      const montantCalc = Math.floor((soldeInfo.solde_disponible * pourcentage) / 100);
      setMontant(montantCalc.toString());
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de votre solde...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="retrait-instantane-tab">
      {/* Message de succès */}
      {success && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-start">
              <div className="text-green-500 text-2xl mr-3">✅</div>
              <div>
                <p className="font-medium text-green-800">Retrait initié avec succès !</p>
                <p className="text-sm text-green-700 mt-1">{success.message}</p>
                <p className="text-sm text-green-600 mt-2">
                  Référence: <span className="font-mono font-medium">{success.reference}</span>
                </p>
                <p className="text-sm text-green-600">
                  Montant à recevoir: <span className="font-bold">{formatMontant(success.montantNet)}</span>
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message d'erreur */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-start">
              <div className="text-red-500 text-xl mr-3">❌</div>
              <div>
                <p className="font-medium text-red-800">Erreur</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Carte de solde disponible */}
      <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white" data-testid="solde-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Solde Disponible</p>
              <p className="text-4xl font-bold" data-testid="solde-disponible">
                {formatMontant(soldeInfo?.solde_disponible)}
              </p>
              <p className="text-green-100 text-xs mt-1">
                Plafond: {soldeInfo?.pourcentage_autorise}% du salaire ({formatMontant(soldeInfo?.plafond_avance)})
              </p>
            </div>
            <div className="text-6xl opacity-80">💰</div>
          </div>
          
          {/* Barre de progression */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-green-100 mb-1">
              <span>Utilisé: {formatMontant(soldeInfo?.montant_retire_mois)}</span>
              <span>Disponible: {formatMontant(soldeInfo?.solde_disponible)}</span>
            </div>
            <div className="w-full bg-green-400 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all duration-500"
                style={{ 
                  width: `${Math.min(100, ((soldeInfo?.montant_retire_mois || 0) / (soldeInfo?.plafond_avance || 1)) * 100)}%` 
                }}
              ></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerte période */}
      {!soldeInfo?.periode_active ? (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-start">
              <div className="text-orange-500 text-xl mr-3">⚠️</div>
              <div>
                <p className="font-medium text-orange-800">Période de retrait inactive</p>
                <p className="text-sm text-orange-700 mt-1">
                  Les retraits sont disponibles du 1er au 25 du mois uniquement.
                </p>
                <p className="text-sm text-orange-600 mt-1">
                  Prochain reset: {soldeInfo?.prochain_reset}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start">
              <div className="text-blue-500 text-xl mr-3">ℹ️</div>
              <div>
                <p className="font-medium text-blue-800">Période de retrait active</p>
                <p className="text-sm text-blue-700 mt-1">
                  Vous pouvez effectuer des retraits instantanés vers votre wallet mobile.
                  Une commission de 5% est appliquée sur chaque retrait.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulaire de retrait */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <span className="mr-2">💸</span>
            Retrait Instantané
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Montants prédéfinis */}
            <div>
              <Label>Montants rapides</Label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[25, 50, 75, 100].map((pct) => (
                  <Button
                    key={pct}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMontantPredefini(pct)}
                    disabled={!soldeInfo?.periode_active || submitting}
                    className="text-sm"
                    data-testid={`btn-${pct}pct`}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
            </div>

            {/* Montant */}
            <div>
              <Label htmlFor="montant">Montant à retirer (GNF) *</Label>
              <Input
                id="montant"
                type="number"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                placeholder="Ex: 100000"
                min="10000"
                max={soldeInfo?.solde_disponible || 0}
                required
                disabled={!soldeInfo?.periode_active || submitting}
                data-testid="input-montant"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum: 10 000 GNF | Maximum: {formatMontant(soldeInfo?.solde_disponible)}
              </p>
            </div>

            {/* Type de wallet */}
            <div>
              <Label htmlFor="walletType">Type de Wallet *</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {walletsDisponibles.map((wallet) => (
                  <button
                    key={wallet.value}
                    type="button"
                    onClick={() => setWalletType(wallet.value)}
                    disabled={!soldeInfo?.periode_active || submitting}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      walletType === wallet.value
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    } ${!soldeInfo?.periode_active || submitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    data-testid={`wallet-${wallet.value}`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <span className="text-2xl">{wallet.icon}</span>
                      <span className="font-medium">{wallet.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Numéro de wallet */}
            <div>
              <Label htmlFor="walletNumber">Numéro du Wallet *</Label>
              <Input
                id="walletNumber"
                type="tel"
                value={walletNumber}
                onChange={(e) => setWalletNumber(e.target.value.replace(/\D/g, ''))}
                placeholder="Ex: 621234567"
                minLength="9"
                maxLength="15"
                required
                disabled={!soldeInfo?.periode_active || submitting}
                data-testid="input-wallet-number"
              />
              <p className="text-xs text-gray-500 mt-1">
                Numéro de téléphone associé à votre {walletsDisponibles.find(w => w.value === walletType)?.label}
              </p>
            </div>

            {/* Récapitulatif */}
            {montant && parseFloat(montant) >= 10000 && (
              <Card className="bg-gray-50 border-gray-200" data-testid="recap-card">
                <CardContent className="p-4">
                  <h4 className="font-medium text-gray-900 mb-3">📊 Récapitulatif</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Montant demandé:</span>
                      <span className="font-medium text-gray-900">{formatMontant(parseFloat(montant))}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Commission ALO (5%):</span>
                      <span className="font-medium text-orange-600">- {formatMontant(commission)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="text-gray-900 font-medium">Montant à recevoir:</span>
                      <span className="font-bold text-green-600 text-lg">{formatMontant(montantNet)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Avertissement */}
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4">
                <div className="flex items-start">
                  <div className="text-yellow-500 text-xl mr-3">⚠️</div>
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Important</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Le retrait est <strong>instantané</strong> et sera crédité sur votre wallet en quelques minutes</li>
                      <li>Vérifiez bien le numéro de wallet avant de confirmer</li>
                      <li>Une commission de 5% est déduite automatiquement</li>
                      <li>Les retraits sont disponibles du 1er au 25 du mois</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Boutons */}
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setMontant('');
                  setWalletNumber('');
                  setError(null);
                  setSuccess(null);
                }}
                disabled={submitting}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={!soldeInfo?.periode_active || submitting || !soldeInfo?.solde_disponible}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="btn-submit-withdrawal"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Traitement...
                  </>
                ) : (
                  <>💸 Retirer maintenant</>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default RetraitInstantaneTab;
